import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import host from "../../global";
import usePurchaseOptions from "../../hooks/usePurchaseOptions";
import GetMoreChoices, { getMoreOptions } from "./GetMoreChoices";
import { formatDate, formatCount, CREDIT_WORDS } from "./billingFormat";

/**
 * The heads-up: shown to an org admin once per plan per credit, when 80% is
 * used and the current pace runs out at least a week before the plan ends.
 * The server decides all of that (purchase-options.heads_up); this only shows
 * it and records that it was seen.
 */
export default function UsageHeadsUp({ suppressed = false }) {
  const navigate = useNavigate();
  const { options } = usePurchaseOptions({ enabled: !suppressed });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [packs, setPacks] = useState(1);
  const marked = useRef(false);

  const heads = options?.heads_up;

  useEffect(() => {
    if (!heads || suppressed || marked.current) return;
    const { preferred, packs: suggested } = getMoreOptions(options);
    setSelected(preferred);
    setPacks(suggested);
    setOpen(true);
    marked.current = true;
    // Seen once is enough: "Remind me later" doesn't bring it back this plan.
    axios.post(
      `${host}/usage-alerts/seen`,
      { credit: heads.credit },
      { headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` } }
    ).catch(() => {});
  }, [heads, options, suppressed]);

  if (!open || !heads) return null;

  const words = CREDIT_WORDS[heads.credit] || CREDIT_WORDS.interviews;
  const { upgrade, topup } = getMoreOptions(options);

  const proceed = () => {
    setOpen(false);
    if (selected === "topup") navigate(`/org-admin/usage?topup=${packs}`);
    else if (selected === "upgrade" && upgrade) navigate(`/org-admin/usage?plan=${upgrade.plan_id}`);
    else navigate("/org-admin/usage");
  };

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="bg-white dark:bg-[#20202B] rounded-2xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-[17px] font-semibold text-gray-900 dark:text-white">
          You've used {formatCount(heads.used)} of your {formatCount(heads.total)} {words.plural}
        </h2>
        <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
          At your current pace you'll run out around {formatDate(heads.runs_out_on, { year: false })}.
          {" "}Your plan runs until {formatDate(options.current?.ends_on)}.
        </p>
        {(upgrade || topup) && (
          <div className="mt-4">
            <GetMoreChoices
              options={options}
              selected={selected}
              onSelect={setSelected}
              packs={packs}
              onPacks={setPacks}
            />
          </div>
        )}
        <div className="mt-5 flex gap-2">
          <button
            onClick={proceed}
            className="flex-1 py-2.5 rounded-full text-[13px] font-semibold text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
          >
            Continue
          </button>
          <button
            onClick={() => setOpen(false)}
            className="flex-1 py-2.5 rounded-full text-[13px] font-semibold border border-[#EBEAED] dark:border-gray-700 text-gray-600 dark:text-gray-300"
          >
            Remind me later
          </button>
        </div>
      </div>
    </div>
  );
}
